import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, HelpCircle, CheckCircle, AlertTriangle, Info, Lock, Search, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';

const EmployerFAQ = () => {
  const faqs = [
    {
      question: 'What does "License Verified" mean?',
      answer: 'License Verified means the candidate\'s professional license was reviewed against official state licensing board records and confirmed to be active and in good standing at the time of verification.',
      icon: CheckCircle,
      iconColor: 'text-green-600'
    },
    {
      question: 'Who performs the verification?',
      answer: 'License verification is completed by INeedASocialWorker.com using publicly available state licensing board databases provided by each state.',
      icon: Search,
      iconColor: 'text-primary'
    },
    {
      question: 'Does verification replace our credentialing process?',
      answer: 'No. License verification is provided as a screening aid only. Employers should continue to follow their own credentialing, background check, and onboarding procedures.',
      icon: AlertTriangle,
      iconColor: 'text-amber-600'
    },
    {
      question: 'Are license numbers shared publicly?',
      answer: 'No. License numbers are used only for verification purposes and are not displayed publicly to protect candidate privacy.',
      icon: Lock,
      iconColor: 'text-primary'
    },
    {
      question: 'How current is the verification?',
      answer: 'Verification reflects the license status at the time of review. Licenses can change, so employers may request updated verification during hiring.',
      icon: Clock,
      iconColor: 'text-blue-600'
    },
    {
      question: 'What does "Self-Reported License" mean?',
      answer: 'Self-reported licenses are entered by candidates but have not yet been reviewed. These candidates do not receive a Verified License badge.',
      icon: Info,
      iconColor: 'text-gray-500'
    },
    {
      question: 'Can we choose to see only verified candidates?',
      answer: 'Yes. Employers can filter search results to show Verified License Only candidates when browsing the candidate database.',
      icon: CheckCircle,
      iconColor: 'text-green-600'
    },
    {
      question: 'Are federal or government jobs handled differently?',
      answer: 'Federal job listings sourced from USAJOBS are displayed separately with clear "Federal Job via USAJobs.gov" badges. License verification applies only to candidate profiles, not government postings.',
      icon: Shield,
      iconColor: 'text-blue-600'
    },
    {
      question: 'Is there an extra cost to view verified candidates?',
      answer: 'Verification availability depends on your employer plan. All subscription plans include access to the verified-only filter as part of candidate browsing features.',
      icon: HelpCircle,
      iconColor: 'text-secondary'
    },
    {
      question: 'What happens if a license expires?',
      answer: 'If a verified license expires or requires re-verification, the badge is removed and marked "Verification Expired" until reviewed again.',
      icon: AlertTriangle,
      iconColor: 'text-amber-600'
    }
  ];

  const badgeStatuses = [
    { status: 'License Verified', description: 'Reviewed and confirmed via state board records', color: 'bg-green-100 text-green-800' },
    { status: 'Self-Reported License', description: 'Entered by candidate, not yet reviewed', color: 'bg-gray-100 text-gray-600' },
    { status: 'Verification Expired', description: 'Re-verification recommended', color: 'bg-amber-100 text-amber-800' },
    { status: 'Unverified', description: 'No verification requested', color: 'bg-gray-50 text-gray-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <Seo
        title="Employer FAQ & License Verification"
        description="Answers for employers on I Need A Social Worker — what License Verified means, how candidate license verification works, credentialing, and privacy of license data."
        path="/employer-faq"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/for-employers" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Employers
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            Employer Resources
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            License Verification FAQ
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Everything employers need to know about our candidate license verification system.
          </p>
        </div>

        {/* Trust Banner */}
        <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-start gap-4">
            <Shield className="w-10 h-10 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-semibold mb-2">License Verification You Can Trust</h2>
              <p className="text-white/90">
                INeedASocialWorker.com helps employers quickly identify licensed professionals by offering an optional 
                Verified License indicator on candidate profiles. This process confirms the license was active and 
                in good standing at the time of review.
              </p>
            </div>
          </div>
        </div>

        {/* Badge Legend */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" />
            Badge Status Legend
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {badgeStatuses.map((badge, index) => (
              <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                  {badge.status}
                </span>
                <span className="text-sm text-gray-600">{badge.description}</span>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const IconComponent = faq.icon;
            return (
              <div key={index} className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg bg-gray-50 ${faq.iconColor}`}>
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Important Disclaimer */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">Important Note for Employers</h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                INeedASocialWorker.com is not a licensing authority. License verification reflects publicly available 
                records at the time of review and does not replace an employer's own credentialing or onboarding process.
                <br /><br />
                <strong>License verification reflects public records at the time of review and does not guarantee ongoing licensure status.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <h3 className="font-serif text-2xl font-bold text-gray-900 mb-4">
            Ready to Find Verified Candidates?
          </h3>
          <p className="text-gray-600 mb-6">
            Subscribe to access our candidate database with verified license filters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/for-employers">
              <Button className="bg-primary hover:bg-primary/90 text-white px-8">
                View Pricing Plans
              </Button>
            </Link>
            <Link to="/register?type=employer">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white px-8">
                Create Employer Account
              </Button>
            </Link>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          Have more questions? Contact us at{' '}
          <a href="mailto:support@ineedasocialworker.com" className="text-primary hover:underline">
            support@ineedasocialworker.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default EmployerFAQ;
